import { useEffect, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';
import type { OwnerProfile, OfflineSummary, RegionId } from '../game/types';
import { GameStore } from '../game/GameStore';
import { applyOfflineProgress, createInitialState } from '../game/simulation';
import { hasSave, importSave, loadGame, saveGame } from '../game/persistence';
import { GameShell } from './GameShell';
import { TitleScreen } from './TitleScreen';
import { reportPublicPageMetrics } from '../analytics';

export function App() {
  const [store, setStore] = useState<GameStore | null>(null);
  const [canContinue, setCanContinue] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [offlineSummary, setOfflineSummary] = useState<OfflineSummary | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateServiceWorker, setUpdateServiceWorker] = useState<((reloadPage?: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    reportPublicPageMetrics();
    void hasSave().then(setCanContinue);
  }, []);
  useEffect(() => {
    const updater = registerSW({ onNeedRefresh: () => setUpdateAvailable(true) });
    setUpdateServiceWorker(() => updater);
  }, []);

  const startNew = (region: RegionId, owner: OwnerProfile) => {
    const next = createInitialState(region, owner);
    void saveGame(next);
    setOfflineSummary(null);
    setStore(new GameStore(next));
  };

  const continueGame = async () => {
    try {
      const loaded = await loadGame();
      if (!loaded) { setMessage('No valid local save was found.'); setCanContinue(false); return; }
      const offline = applyOfflineProgress(loaded.state);
      if (loaded.recoveredFromBackup) setMessage('The rolling backup recovered this vintage.');
      setOfflineSummary(offline.summary);
      setStore(new GameStore(offline.state));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'The save could not be loaded.');
    }
  };

  const handleImport = async (file: File) => {
    try {
      const state = await importSave(file);
      setCanContinue(true);
      setMessage('Save imported. Welcome back to the valley.');
      setStore(new GameStore(state));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'That file is not a valid save.');
    }
  };

  if (!store) return <TitleScreen canContinue={canContinue} message={message} onContinue={() => void continueGame()} onNewGame={startNew} onImport={(file) => void handleImport(file)} />;
  return <GameShell store={store} offlineSummary={offlineSummary} updateAvailable={updateAvailable} onUpdate={() => void updateServiceWorker?.(true)} onExit={() => { void saveGame(store.getState()); store.stop(); setCanContinue(true); setStore(null); }} />;
}
